/*
 * Local conversation history, in IndexedDB.
 *
 * Shared by both customer renderers — the Next.js app in apps/web and the
 * Ionic/Capacitor app in apps/mobile. It is a package rather than a file in
 * either of them because "keep 25 conversations per business, newest first,
 * and forget them on request" is one behaviour: two copies of it would drift,
 * and the half that drifts is a privacy promise.
 *
 * Pure browser code. No React, no Next, no Capacitor — it must stay importable
 * by any renderer, including one that has not been written yet.
 *
 * The chat server hands back one step at a time and keeps no transcript, so
 * "your previous conversations" can only exist on the device. IndexedDB rather
 * than localStorage because a transcript is structured and unbounded — a
 * booking with a dozen turns serialised into a single string key is a synchronous
 * parse on the main thread every time the sidebar renders.
 *
 * PRIVACY. What is stored here is what the customer typed: usually a name and a
 * mobile number. That is fine on a personal phone and NOT fine on a tablet
 * sitting on a counter, where the next customer would find the last one's
 * details in the sidebar. Two things follow, and both are load-bearing:
 *
 *   1. History is written only on the `app` surface — the business's own page,
 *      which is the one a customer opens on their own device. The embedded
 *      widget runs on somebody else's website and stores nothing, ever.
 *   2. `clear()` is exposed in the UI, not just here.
 *
 * If a tenant runs their app on shared hardware this is still the wrong default
 * for them, and the honest fix is a per-tenant switch rather than a comment.
 *
 * EVERY function is failure-tolerant by design. IndexedDB is unavailable in
 * private windows on some engines, blocked by "block third-party data"
 * settings, and throws rather than returning null when it is. History is a
 * convenience, so a store that cannot open degrades to no history at all and
 * the conversation itself is never affected.
 */

const DB_NAME = 'blooq-chat'
const DB_VERSION = 1
const STORE = 'sessions'

/** How many conversations to keep per business before the oldest is dropped. */
const KEEP = 25

export interface StoredTurn {
  prompt: string
  answer?: string
}

export interface StoredSession {
  /** The server's session id. Unique, and already the natural key. */
  id: string
  /**
   * Which business this belongs to. A customer can have conversations with
   * several, and one business must never list another's.
   */
  slug: string
  /** A short label for the list — the first real answer, or the first prompt. */
  title: string
  turns: StoredTurn[]
  startedAt: number
  updatedAt: number
  /** True once the conversation reached a terminal step. */
  done: boolean
}

function available(): boolean {
  try {
    return typeof indexedDB !== 'undefined'
  } catch {
    // Accessing the global itself throws where site data is blocked outright.
    return false
  }
}

let dbPromise: Promise<IDBDatabase | null> | null = null

function open(): Promise<IDBDatabase | null> {
  if (!available()) return Promise.resolve(null)
  if (dbPromise) return dbPromise

  dbPromise = new Promise<IDBDatabase | null>((resolve) => {
    let request: IDBOpenDBRequest
    try {
      request = indexedDB.open(DB_NAME, DB_VERSION)
    } catch {
      return resolve(null)
    }

    request.onupgradeneeded = () => {
      const db = request.result
      if (!db.objectStoreNames.contains(STORE)) {
        const store = db.createObjectStore(STORE, { keyPath: 'id' })
        // The sidebar always asks "this business, newest first", so that is the
        // index rather than two and a sort in JS.
        store.createIndex('slug_updatedAt', ['slug', 'updatedAt'])
      }
    }

    request.onsuccess = () => resolve(request.result)
    request.onerror = () => resolve(null)
    // Another tab holding an old version open. Resolving null rather than
    // hanging is what keeps the sidebar from waiting forever on a promise.
    request.onblocked = () => resolve(null)
  })

  return dbPromise
}

function tx<T>(
  mode: IDBTransactionMode,
  run: (store: IDBObjectStore) => IDBRequest<T>,
): Promise<T | null> {
  return open().then(
    (db) =>
      new Promise<T | null>((resolve) => {
        if (!db) return resolve(null)
        try {
          const transaction = db.transaction(STORE, mode)
          const request = run(transaction.objectStore(STORE))
          request.onsuccess = () => resolve(request.result)
          request.onerror = () => resolve(null)
          transaction.onabort = () => resolve(null)
        } catch {
          resolve(null)
        }
      }),
  )
}

/**
 * Derive the list label.
 *
 * The first answer the customer actually gave says far more than the first
 * prompt ("How can I help you today?" is the same for every conversation), so
 * it wins where there is one.
 */
export function titleOf(turns: StoredTurn[]): string {
  const answered = turns.find((turn) => turn.answer && turn.answer.trim())
  const raw = answered?.answer ?? turns[0]?.prompt ?? 'Conversation'
  const flat = raw.replace(/\s+/g, ' ').trim()
  return flat.length > 46 ? `${flat.slice(0, 45)}…` : flat
}

/** Write or overwrite one conversation. Never throws. */
export async function putSession(session: StoredSession): Promise<void> {
  await tx('readwrite', (store) => store.put(session) as IDBRequest<IDBValidKey>)
}

/** Newest first, for one business only. */
export async function listSessions(slug: string): Promise<StoredSession[]> {
  const all = await tx<StoredSession[]>(
    'readonly',
    (store) =>
      store
        .index('slug_updatedAt')
        // An unbounded upper key: every timestamp for this slug, no other slug.
        .getAll(IDBKeyRange.bound([slug, -Infinity], [slug, Infinity])) as IDBRequest<
        StoredSession[]
      >,
  )
  if (!all) return []
  return all.sort((a, b) => b.updatedAt - a.updatedAt).slice(0, KEEP)
}

export async function getSession(id: string): Promise<StoredSession | null> {
  const found = await tx<StoredSession>('readonly', (store) => store.get(id) as IDBRequest<StoredSession>)
  return found ?? null
}

export async function deleteSession(id: string): Promise<void> {
  await tx('readwrite', (store) => store.delete(id) as IDBRequest<undefined>)
}

/**
 * Forget everything for one business.
 *
 * Scoped to a slug rather than clearing the store, because a customer clearing
 * their history with one business has not asked to lose it with another.
 */
export async function clearSessions(slug: string): Promise<void> {
  const existing = await listSessions(slug)
  await Promise.all(existing.map((session) => deleteSession(session.id)))
}

/**
 * Drop the oldest once a business has more than KEEP.
 *
 * Called after a write rather than on a timer: the only moment the list can
 * exceed the cap is immediately after something is added to it.
 */
export async function prune(slug: string): Promise<void> {
  const all = await tx<StoredSession[]>(
    'readonly',
    (store) =>
      store
        .index('slug_updatedAt')
        .getAll(IDBKeyRange.bound([slug, -Infinity], [slug, Infinity])) as IDBRequest<
        StoredSession[]
      >,
  )
  if (!all || all.length <= KEEP) return

  const stale = all.sort((a, b) => b.updatedAt - a.updatedAt).slice(KEEP)
  await Promise.all(stale.map((session) => deleteSession(session.id)))
}
